// Configuration mapping view codes to their allowed segmentation labels (quality flags)
// Based on the quality_flag_def table in initialize_db.sql

export interface ViewSegmentationConfig {
  viewCode: string;
  displayName: string;
  allowedSegmentations: {
    flagCode: string;
    displayName: string;
    defaultOn: boolean;
  }[];
}

// Map of view codes to their allowed segmentation labels
export const VIEW_SEGMENTATION_CONFIG: Record<string, ViewSegmentationConfig> = {
  'VIEW__AxialViewOfFetalBladder': {
    viewCode: 'VIEW__AxialViewOfFetalBladder',
    displayName: 'Axial View of Fetal Bladder',
    allowedSegmentations: [
      { flagCode: 'fetal_bladder', displayName: 'Fetal Bladder', defaultOn: false },
      { flagCode: 'heart', displayName: 'Heart', defaultOn: true },
      { flagCode: 'stomach_bubble', displayName: 'Stomach Bubble', defaultOn: true },
      { flagCode: 'abdomen', displayName: 'Abdominal Biometry', defaultOn: true },
    ]
  },
  
  'VIEW__AxialViewOfKidneys': {
    viewCode: 'VIEW__AxialViewOfKidneys',
    displayName: 'Axial View of Kidneys',
    allowedSegmentations: [
      { flagCode: 'fetal_kidneys', displayName: 'Fetal Kidneys', defaultOn: false },
      { flagCode: 'stomach_bubble', displayName: 'Stomach Bubble Not Visible', defaultOn: true },
      { flagCode: 'portal_sinus', displayName: 'Portal Sinus Not Visible', defaultOn: true },
      { flagCode: 'fetal_bladder', displayName: 'Fetal Bladder Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__CrownRumpLengthView': {
    viewCode: 'VIEW__CrownRumpLengthView',
    displayName: 'Crown Rump Length View',
    allowedSegmentations: [
      { flagCode: 'fetal_segmentation', displayName: 'Fetal Segmentation', defaultOn: true },
      { flagCode: 'hard_palate', displayName: 'Hard Palate', defaultOn: true },
      { flagCode: 'nasal_bone', displayName: 'Nasal Bone', defaultOn: true },
      { flagCode: 'abdomen', displayName: 'Bone', defaultOn: true },
    ]
  },
  
  'VIEW__Femoral': {
    viewCode: 'VIEW__Femoral',
    displayName: 'Femoral View',
    allowedSegmentations: [
      { flagCode: 'femur_extremity', displayName: 'Femur Extremities', defaultOn: false },
      { flagCode: 'femur', displayName: 'Femur', defaultOn: false },
    ]
  },
  
  'VIEW__FetalAbdominalStandardPlane': {
    viewCode: 'VIEW__FetalAbdominalStandardPlane',
    displayName: 'Fetal Abdominal Standard Plane',
    allowedSegmentations: [
      { flagCode: 'abdomen', displayName: 'Abdominal Biometry', defaultOn: false },
      { flagCode: 'stomach_bubble', displayName: 'Stomach Bubble', defaultOn: false },
      { flagCode: 'portal_sinus', displayName: 'Portal Sinus', defaultOn: false },
      { flagCode: 'lungs', displayName: 'Lungs Not Visible', defaultOn: true },
      { flagCode: 'fetal_kidneys', displayName: 'Fetal Kidneys Not Visible', defaultOn: true },
      { flagCode: 'heart', displayName: 'Heart Not Visible', defaultOn: true },
      { flagCode: 'left_ventricle', displayName: 'Left Ventricle Not Visible', defaultOn: true },
      { flagCode: 'right_ventricle', displayName: 'Right Ventricle Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__4CH': {
    viewCode: 'VIEW__4CH',
    displayName: 'Four Chamber View',
    allowedSegmentations: [
      { flagCode: 'left_ventricle', displayName: 'Left Ventricle', defaultOn: false },
      { flagCode: 'right_ventricle', displayName: 'Right Ventricle', defaultOn: false },
      { flagCode: 'left_atrium', displayName: 'Left Atrium', defaultOn: false },
      { flagCode: 'right_atrium', displayName: 'Right Atrium', defaultOn: false },
      { flagCode: 'inter_atrial_septum', displayName: 'Inter Atrial Septum', defaultOn: false },
      { flagCode: 'mitral_valve', displayName: 'Mitral Valve', defaultOn: false },
      { flagCode: 'tricuspid_valve', displayName: 'Tricuspid Valve', defaultOn: false },
      { flagCode: 'heart', displayName: 'Heart', defaultOn: false },
      { flagCode: 'interventricular_septum', displayName: 'Interventricular Septum', defaultOn: false },
      { flagCode: 'abdomen', displayName: 'Abdominal Biometry Not Visible', defaultOn: true },
      { flagCode: 'fetal_kidneys', displayName: 'Fetal Kidneys Not Visible', defaultOn: true },
      { flagCode: 'stomach_bubble', displayName: 'Stomach Bubble Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__LVOT': {
    viewCode: 'VIEW__LVOT',
    displayName: 'LVOT View',
    allowedSegmentations: [
      { flagCode: 'heart', displayName: 'Heart', defaultOn: false },
      { flagCode: 'left_ventricle', displayName: 'Left Ventricle', defaultOn: false },
      { flagCode: 'right_ventricle', displayName: 'Right Ventricle', defaultOn: false },
      { flagCode: 'left_atrium', displayName: 'Left Atrium', defaultOn: false },
      { flagCode: 'mitral_valve', displayName: 'Mitral Valve', defaultOn: false },
      { flagCode: 'interventricular_septum', displayName: 'Interventricular Septum', defaultOn: false },
      { flagCode: 'aorta', displayName: 'Aorta', defaultOn: false },
      { flagCode: 'right_atrium', displayName: 'Right Atrium Not Visible', defaultOn: true },
      { flagCode: 'pulmonary_artery', displayName: 'Pulmonary Artery Not Visible', defaultOn: true },
      { flagCode: 'tricuspid_valve', displayName: 'Tricuspid Valve Not Visible', defaultOn: true },
      { flagCode: 'superior_vena_cava', displayName: 'Superior Vena Cava Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__NuchalTranslucencyViewT1': {
    viewCode: 'VIEW__NuchalTranslucencyViewT1',
    displayName: 'Nuchal Translucency View T1',
    allowedSegmentations: [
      { flagCode: 'fetal_brain', displayName: 'Nuchal Translucency', defaultOn: false },
      { flagCode: 'nasal_bone', displayName: 'Nasal Bone', defaultOn: false },
      { flagCode: 'hard_palate', displayName: 'Hard Palate', defaultOn: false },
      { flagCode: 'abdomen', displayName: 'Abdominal Biometry Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__Orbits': {
    viewCode: 'VIEW__Orbits',
    displayName: 'Orbits View',
    allowedSegmentations: [
      { flagCode: 'orbit', displayName: 'Orbits', defaultOn: false },
      { flagCode: 'ventricle', displayName: 'Ventricle Not Visible', defaultOn: true },
      { flagCode: 'mandible', displayName: 'Mandible Not Visible', defaultOn: true },
      { flagCode: 'fetal_spine', displayName: 'Fetal Spine Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__RVOT': {
    viewCode: 'VIEW__RVOT',
    displayName: 'RVOT View',
    allowedSegmentations: [
      { flagCode: 'right_ventricle', displayName: 'Right Ventricle', defaultOn: false },
      { flagCode: 'pulmonary_artery', displayName: 'Pulmonary Artery', defaultOn: false },
      { flagCode: 'tricuspid_valve', displayName: 'Tricuspid Valve', defaultOn: false },
      { flagCode: 'heart', displayName: 'Heart', defaultOn: false },
      { flagCode: 'superior_vena_cava', displayName: 'Superior Vena Cava', defaultOn: false },
      { flagCode: 'aorta', displayName: 'Aorta', defaultOn: false },
      { flagCode: 'left_atrium', displayName: 'Left Atrium Not Visible', defaultOn: true },
      { flagCode: 'left_ventricle', displayName: 'Left Ventricle Not Visible', defaultOn: true },
      { flagCode: 'mitral_valve', displayName: 'Mitral Valve Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__SagittalViewOfCervixWithPlacenta': {
    viewCode: 'VIEW__SagittalViewOfCervixWithPlacenta',
    displayName: 'Sagittal View of Cervix with Placenta',
    allowedSegmentations: [
      { flagCode: 'cervix', displayName: 'Cervix', defaultOn: false },
      { flagCode: 'maternal_bladder', displayName: 'Maternal Bladder', defaultOn: false },
      { flagCode: 'placenta', displayName: 'Placenta', defaultOn: false },
    ]
  },
  
  // 'VIEW__SagittalViewOfTheEntireSpine': {
  //   viewCode: 'VIEW__SagittalViewOfTheEntireSpine',
  //   displayName: 'Sagittal View of the Entire Spine',
  //   allowedSegmentations: [
  //     { flagCode: 'fetal_spine', displayName: 'Fetal Spine', defaultOn: false },
  //     { flagCode: 'abdomen', displayName: 'Abdominal Biometry Not Visible', defaultOn: true },
  //     { flagCode: 'heart', displayName: 'Heart Not Visible', defaultOn: true },
  //     { flagCode: 'fetal_brain', displayName: 'Cephalic Plane Not Visible', defaultOn: true },
  //   ]
  // },
  
  'VIEW__ThreeVesselView': {
    viewCode: 'VIEW__ThreeVesselView',
    displayName: 'Three Vessel View',
    allowedSegmentations: [
      { flagCode: 'superior_vena_cava', displayName: 'Superior Vena Cava', defaultOn: false },
      { flagCode: 'aorta', displayName: 'Aorta', defaultOn: false },
      { flagCode: 'pulmonary_artery', displayName: 'Pulmonary Artery', defaultOn: false },
      { flagCode: 'left_ventricle', displayName: 'Left Ventricle Not Visible', defaultOn: true },
      { flagCode: 'right_ventricle', displayName: 'Right Ventricle Not Visible', defaultOn: true },
      { flagCode: 'left_atrium', displayName: 'Left Atrium Not Visible', defaultOn: true },
      { flagCode: 'right_atrium', displayName: 'Right Atrium Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__Transcerebellar': {
    viewCode: 'VIEW__Transcerebellar',
    displayName: 'Transcerebellar View',
    allowedSegmentations: [
      { flagCode: 'thalamus', displayName: 'Thalamus', defaultOn: false },
      { flagCode: 'vermis', displayName: 'Vermis', defaultOn: false },
      { flagCode: 'cerebellum', displayName: 'Cerebellum', defaultOn: false },
      { flagCode: 'cisterna_magna', displayName: 'Cisterna Magna', defaultOn: false },
      { flagCode: 'cavum_septum_pellucidum', displayName: 'Cavum Septum Pellucidum', defaultOn: false },
      { flagCode: 'fetal_brain', displayName: 'Cephalic Plane', defaultOn: false },
    ]
  },
  
  'VIEW__Transthalamic': {
    viewCode: 'VIEW__Transthalamic',
    displayName: 'Transthalamic View',
    allowedSegmentations: [
      { flagCode: 'cavum_septum_pellucidum', displayName: 'Cavum Septum Pellucidum', defaultOn: false },
      { flagCode: 'thalamus', displayName: 'Thalamus', defaultOn: false },
      { flagCode: 'hippocampal_gyri', displayName: 'Hippocampal Gyri', defaultOn: false },
      { flagCode: 'fetal_brain', displayName: 'Cephalic Plane', defaultOn: false },
      { flagCode: 'sylvian_fissure', displayName: 'Sylvian Fissure', defaultOn: false },
      { flagCode: 'cerebellum', displayName: 'Cerebellum Not Visible', defaultOn: true },
      { flagCode: 'vermis', displayName: 'Vermis Not Visible', defaultOn: true },
      { flagCode: 'cisterna_magna', displayName: 'Cisterna Magna Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__Transventricular': {
    viewCode: 'VIEW__Transventricular',
    displayName: 'Transventricular View',
    allowedSegmentations: [
      { flagCode: 'ventricle', displayName: 'Ventricle', defaultOn: false },
      { flagCode: 'choroid_plexus', displayName: 'Choroid Plexus', defaultOn: false },
      { flagCode: 'cavum_septum_pellucidum', displayName: 'Cavum Septum Pellucidum', defaultOn: false },
      { flagCode: 'fetal_brain', displayName: 'Cephalic Plane', defaultOn: false },
      { flagCode: 'thalamus', displayName: 'Thalamus Not Visible', defaultOn: true },
      { flagCode: 'cerebellum', displayName: 'Cerebellum Not Visible', defaultOn: true },
      { flagCode: 'vermis', displayName: 'Vermis Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__LowerLimb': {
    viewCode: 'VIEW__LowerLimb',
    displayName: 'Lower Limb View',
    allowedSegmentations: [
      { flagCode: 'femur', displayName: 'Femur', defaultOn: false },
      { flagCode: 'leg_bones', displayName: 'Leg Bones', defaultOn: false },
      { flagCode: 'foot', displayName: 'Foot', defaultOn: false },
      { flagCode: 'arm_humerus', displayName: 'Arm Humerus Not Visible', defaultOn: true },
      { flagCode: 'forearm_bones', displayName: 'Forearm Bones Not Visible', defaultOn: true },
      { flagCode: 'hand', displayName: 'Hand Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__UpperLimb': {
    viewCode: 'VIEW__UpperLimb',
    displayName: 'Upper Limb View',
    allowedSegmentations: [
      { flagCode: 'arm_humerus', displayName: 'Arm Humerus', defaultOn: false },
      { flagCode: 'forearm_bones', displayName: 'Forearm Bones', defaultOn: false },
      { flagCode: 'hand', displayName: 'Hand', defaultOn: false },
      { flagCode: 'femur', displayName: 'Femur Not Visible', defaultOn: true },
      { flagCode: 'leg_bones', displayName: 'Leg Bones Not Visible', defaultOn: true },
      { flagCode: 'foot', displayName: 'Foot Not Visible', defaultOn: true },
    ]
  },
  
  'VIEW__AmnioticFluidPocket': {
    viewCode: 'VIEW__AmnioticFluidPocket',
    displayName: 'Amniotic Fluid Pocket View',
    allowedSegmentations: [
      { flagCode: 'amniotic_fluid', displayName: 'Amniotic Fluid', defaultOn: false },
    ]
  },
};

// Helper function to get allowed segmentations for a view
export function getAllowedSegmentationsForView(viewCode: string): ViewSegmentationConfig['allowedSegmentations'] {
  const config = VIEW_SEGMENTATION_CONFIG[viewCode];
  return config?.allowedSegmentations || [];
}

// Helper function to get flag codes only for a view
export function getAllowedFlagCodesForView(viewCode: string): string[] {
  const config = VIEW_SEGMENTATION_CONFIG[viewCode];
  return config?.allowedSegmentations.map(s => s.flagCode) || [];
}

// Helper function to check if a flag code is allowed for a view
export function isFlagCodeAllowedForView(viewCode: string, flagCode: string): boolean {
  const allowedCodes = getAllowedFlagCodesForView(viewCode);
  return allowedCodes.includes(flagCode);
}

// Get all unique flag codes across all views (for backwards compatibility)
export function getAllFlagCodes(): string[] {
  const allCodes = new Set<string>();
  Object.values(VIEW_SEGMENTATION_CONFIG).forEach(config => {
    config.allowedSegmentations.forEach(seg => {
      allCodes.add(seg.flagCode);
    });
  });
  return Array.from(allCodes);
}

